import cn from "classnames";
import { FC, useRef, ReactNode, useEffect, PropsWithChildren } from "react";
import { useClientRect } from "src/hooks/useClientRect";
import { useSticky } from "src/hooks/useSticky";
import {
  StyledSticky,
  StickySentinel,
  StyledStickyHeaderShadow,
} from "./Sticky.styled";

interface Props {
  children: ReactNode;
  offsetTop?: number;
  onChange?: (mode: boolean) => void;
  top?: number;
  boxshadowMode?: string;
  isHeader?: boolean;
  hideShadowOnMobile?: boolean;
}

const Sticky: FC<PropsWithChildren<Props>> = ({
  offsetTop = 0,
  onChange,
  top,
  boxshadowMode,
  children,
  isHeader,
  hideShadowOnMobile,
}) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const sticky = useSticky(sentinelRef, offsetTop);

  const contentHeight = useClientRect(contentRef).height;
  const headerOffset = Math.ceil(contentHeight);

  useEffect(() => {
    if (!isHeader || !headerOffset) {
      return;
    }

    const root = document.documentElement;
    root.style.setProperty("--sticky-header-offset", `${headerOffset}px`);

    return () => {
      root.style.removeProperty("--sticky-header-offset");
    };
  }, [isHeader, headerOffset]);

  const getStyles = () => {
    if (typeof window !== undefined && sticky) {
      return { opacity: 1 };
    }
    return {};
  };

  useEffect(() => {
    if (onChange) {
      onChange(sticky);
    }
  }, [sticky, onChange]);

  return (
    <StyledSticky
      className={cn({ Sticky_sticky: sticky })}
      height={contentHeight}
      top={top}
      boxshadowMode={boxshadowMode}
      isHeader={isHeader}
    >
      <StickySentinel ref={sentinelRef} aria-hidden="true" />
      <div
        ref={contentRef}
        className="Sticky__content"
        style={{ ...getStyles() }}
      >
        {children}
      </div>
      {isHeader && sticky && (
        <StyledStickyHeaderShadow
          headerTop={top ?? 0}
          fallbackHeight={headerOffset}
          hideOnMobile={hideShadowOnMobile}
        />
      )}
    </StyledSticky>
  );
};

export default Sticky;
